import React from 'react';
import { connect } from 'react-redux';
import { Grid, Row, Col } from 'react-bootstrap';
import { translate } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';

import Layout from '../../components/layout/Layout';
import Card from '../../components/card/Card';
import { Button, Form, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { listBuildingType } from '../../redux/building_type/building_type_actions';
import { listProvinces } from '../../redux/provinces/province_actions';
import { listEvaluationPurpose } from '../../redux/references/evaluation_purpose_actions';
import { defaultRejectHandler } from '../../utils/utility';
import { serializeParams } from '../../utils/rest-service';
import Constants from '../../config/constants';
import '../../components/default.css';

const EVALUATION_STATUS_OPTIONS = [
  { id: 'all_passed', labelTh: 'ผ่านเกณฑ์ทั้งหมด', labelEn: 'Passed all criteria' },
  { id: 'system_passed', labelTh: 'ผ่านเกณฑ์รายระบบ', labelEn: 'Passed by system' },
  { id: 'energy_passed', labelTh: 'ผ่านเกณฑ์พลังงานโดยรวม', labelEn: 'Passed overall energy' },
  { id: 'both_failed', labelTh: 'ไม่ผ่านทั้งสองเกณฑ์', labelEn: 'Failed both criteria' },
];

const CHART_COLORS = [
  '#5B8FF9', '#61DDAA', '#F6903D', '#7262FD',
  '#78D3F8', '#F6BD16', '#65789B', '#F08BB4',
];

const BAR_CHART_CONFIGS = [
  {
    key: 'saving_energy',
    titleTh: 'ผลประหยัด',
    titleEn: 'Energy Savings',
    unitTh: 'GWh/ปี',
    unitEn: 'GWh/y',
    color: '#5B8FF9',
    convert: v => v / 1e6,
  },
  {
    key: 'saving_ktoe',
    titleTh: 'ลดการใช้พลังงานไฟฟ้า',
    titleEn: 'Reduced Electricity',
    unitTh: 'ktoe/ปี',
    unitEn: 'ktoe/y',
    color: '#61DDAA',
    convert: v => v,
  },
  {
    key: 'cost_saving',
    titleTh: 'ประหยัดค่าใช้จ่าย',
    titleEn: 'Cost Saving',
    unitTh: 'ล้านบาท/ปี',
    unitEn: 'M THB/y',
    color: '#F6903D',
    convert: v => v / 1e6,
  },
  {
    key: 'co2_reduction',
    titleTh: 'ลดการใช้ก๊าซ CO₂',
    titleEn: 'CO₂ Reduction',
    unitTh: 'tons CO₂/ปี',
    unitEn: 'tons CO₂/y',
    color: '#F5222D',
    convert: v => v / 1e6,
  },
];

class ExecutiveDashboardPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      savingSummary: null,
      buildingCount: null,
      loading: false,
      selectedBuildingTypeIds: [],
      selectedProvinceIds: [],
      selectedEvaluationStatuses: [],
      selectedEvaluationPurposeIds: [],
      areaMin: '',
      areaMax: '',
      dateFrom: '',
      dateTo: '',
      openDropdown: null,
      filterExpanded: false,
    };
  }

  componentDidMount() {
    this.props.listBuildingTypes();
    this.props.listProvinces();
    this.props.listEvaluationPurpose();
    this.loadData();
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
  }

  handleDocumentMouseDown = (e) => {
    if (this.filterRef && !this.filterRef.contains(e.target)) {
      if (this.state.openDropdown) {
        this.setState({ openDropdown: null });
      }
    }
  }

  buildFilterParams() {
    const params = { status: 'SUB' };
    if (this.state.selectedProvinceIds.length > 0) {
      params.province_id = this.state.selectedProvinceIds;
    }
    if (this.state.selectedBuildingTypeIds.length > 0) {
      params.building_type_id = this.state.selectedBuildingTypeIds;
    }
    if (this.state.selectedEvaluationStatuses.length > 0) {
      params.evaluation_status = this.state.selectedEvaluationStatuses;
    }
    if (this.state.selectedEvaluationPurposeIds.length > 0) {
      params.evaluation_purpose = this.state.selectedEvaluationPurposeIds;
    }
    if (this.state.areaMin !== '') {
      params.area_min = this.state.areaMin;
    }
    if (this.state.areaMax !== '') {
      params.area_max = this.state.areaMax;
    }
    if (this.state.dateFrom) {
      params.date_from = this.state.dateFrom;
    }
    if (this.state.dateTo) {
      params.date_to = this.state.dateTo;
    }
    return params;
  }

  loadData() {
    const accessToken = localStorage.getItem('access-token');
    const headers = {};
    if (accessToken) {
      headers['Authorization'] = `Token ${accessToken}`;
    }
    const params = this.buildFilterParams();
    this.setState({ loading: true });

    Promise.all([
      axios({
        method: 'get',
        url: Constants.API_PROJECT_STATS_SAVING_SUMMARY,
        params,
        paramsSerializer: serializeParams,
        headers,
      }),
      axios({
        method: 'get',
        url: Constants.API_PROJECT_STATS,
        params,
        paramsSerializer: serializeParams,
        headers,
      }),
    ])
      .then(([summaryRes, statsRes]) => {
        const statsData = statsRes.data;
        const count =
          statsData.building_count != null ? statsData.building_count :
          statsData.project_count != null ? statsData.project_count :
          statsData.count != null ? statsData.count :
          statsData.totals && statsData.totals.count != null ? statsData.totals.count :
          null;
        this.setState({
          savingSummary: summaryRes.data,
          buildingCount: count,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  handleSearch = () => {
    this.loadData();
  }

  handleClearFilters = () => {
    this.setState({
      selectedBuildingTypeIds: [],
      selectedProvinceIds: [],
      selectedEvaluationStatuses: [],
      selectedEvaluationPurposeIds: [],
      areaMin: '',
      areaMax: '',
      dateFrom: '',
      dateTo: '',
      openDropdown: null,
    }, () => {
      this.loadData();
    });
  }

  renderMultiSelectDropdown({ dropdownKey, options, selectedIds, onToggle, getLabel, allLabelTh, allLabelEn }) {
    const lang = this.props.i18n.language.split('-')[0];
    const l = (th, en) => (lang === 'th' ? th : en);
    const isOpen = this.state.openDropdown === dropdownKey;
    const displayText = selectedIds.length === 0
      ? l(allLabelTh || 'ทั้งหมด', allLabelEn || 'All')
      : `${selectedIds.length} ${l('รายการ', 'selected')}`;

    const dropdownStyle = {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1050,
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: 4,
      maxHeight: 220,
      overflowY: 'auto',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    };

    const itemStyle = {
      padding: '6px 12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      userSelect: 'none',
    };

    const handleToggleAll = () => {
      onToggle([]);
      this.setState({ openDropdown: null });
    };

    const handleToggleItem = (id) => {
      const idStr = String(id);
      const newIds = selectedIds.includes(idStr)
        ? selectedIds.filter(x => x !== idStr)
        : [...selectedIds, idStr];
      onToggle(newIds);
    };

    return (
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className="form-control"
          style={{ textAlign: 'left', cursor: 'pointer', background: '#fff' }}
          onClick={() => this.setState({ openDropdown: isOpen ? null : dropdownKey })}
        >
          {displayText}
          <span style={{ float: 'right', color: '#999' }}>&#9662;</span>
        </button>
        {isOpen && (
          <div style={dropdownStyle}>
            <div
              style={{ ...itemStyle, borderBottom: '1px solid #eee', fontWeight: 500 }}
              onClick={handleToggleAll}
            >
              <input type="checkbox" readOnly checked={selectedIds.length === 0} style={{ marginRight: 4 }} />
              {l('ทั้งหมด', 'All')}
            </div>
            {options.map(opt => {
              const idStr = String(opt.id);
              return (
                <div key={opt.id} style={itemStyle} onClick={() => handleToggleItem(opt.id)}>
                  <input type="checkbox" readOnly checked={selectedIds.includes(idStr)} style={{ marginRight: 4 }} />
                  {getLabel(opt)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderSummaryCards() {
    const lang = this.props.i18n.language.split('-')[0];
    const l = (th, en) => (lang === 'th' ? th : en);
    const { savingSummary, buildingCount, loading, filterExpanded } = this.state;

    const format = value => {
      if (value == null || isNaN(value)) return '-';
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const getAll = (key, convert) => {
      if (!savingSummary || savingSummary[key] == null) return null;
      const raw = savingSummary[key]['all'];
      return raw == null ? null : convert(raw);
    };

    const formatPct = value => {
      if (value == null || isNaN(value)) return '-';
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const savingGWh = getAll('saving_energy', v => v / 1e6);
    const savingKtoe = getAll('saving_ktoe', v => v);
    const costMillion = getAll('cost_saving', v => v / 1e6);
    const co2Tons = getAll('co2_reduction', v => v / 1e6);
    const renewableKWh = getAll('renewable_energy', v => v);

    const savingPctAvg = savingSummary ? savingSummary.saving_energy_percent_avg : null;
    const netEpaAvg = savingSummary ? savingSummary.net_epa_avg : null;
    const euiAvg = savingSummary ? savingSummary.eui_avg : null;
    const areaAvg = savingSummary ? savingSummary.area_avg : null;

    const today = new Date();
    const dateStr = today.toLocaleDateString('th-TH', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });

    const statRows = [
      {
        label: l('ผลประหยัด', 'Energy Savings'),
        value: format(savingGWh),
        unit: l('GWh/ปี', 'GWh/y'),
        icon: 'fas fa-bolt',
        color: '#5B8FF9',
      },
      {
        label: l('ลดการใช้พลังงานไฟฟ้า', 'Reduced Electricity'),
        value: format(savingKtoe),
        unit: l('ktoe/ปี', 'ktoe/y'),
        icon: 'fas fa-tint',
        color: '#61DDAA',
      },
      {
        label: l('ประหยัดค่าใช้จ่าย', 'Cost Saving'),
        value: format(costMillion),
        unit: l('ล้านบาท/ปี', 'M THB/y'),
        icon: 'fas fa-piggy-bank',
        color: '#F6903D',
      },
      {
        label: l('ลดการใช้ก๊าซ CO₂', 'CO₂ Reduction'),
        value: format(co2Tons),
        unit: l('tons CO₂/ปี', 'tons CO₂/y'),
        icon: 'fas fa-cloud',
        color: '#F5222D',
      },
      {
        label: l('ค่าการใช้พลังงานโดยรวมเฉลี่ย', 'Avg. Net EPA'),
        value: formatPct(netEpaAvg),
        unit: l('kWh/m²/ปี', 'kWh/m²/y'),
        icon: 'fas fa-chart-line',
        color: '#FA8C16',
      },
      {
        label: l('พลังงานหมุนเวียนรวม', 'Total Renewable Energy'),
        value: format(renewableKWh),
        unit: l('kWh/ปี', 'kWh/y'),
        icon: 'fas fa-leaf',
        color: '#52C41A',
      },
      {
        label: l('% ประหยัดพลังงานเฉลี่ย', 'Avg. Energy Saving %'),
        value: formatPct(savingPctAvg),
        unit: '%',
        icon: 'fas fa-percentage',
        color: '#7262FD',
      },
      {
        label: l('EUI เฉลี่ย', 'Avg. EUI'),
        value: formatPct(euiAvg),
        unit: l('kWh/m²/ปี', 'kWh/m²/y'),
        icon: 'fas fa-thermometer-half',
        color: '#F6BD16',
      },
      {
        label: l('พื้นที่ใช้สอยเฉลี่ย', 'Avg. Usable Area'),
        value: formatPct(areaAvg),
        unit: l('ตร.ม.', 'm²'),
        icon: 'fas fa-ruler-combined',
        color: '#13C2C2',
      },
    ];

    return (
      <div>
        {/* Meta info row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0 14px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-building" style={{ color: '#5B8FF9', fontSize: 18 }} />
            <span style={{ fontSize: 15, color: '#555' }}>{l('จำนวนอาคาร', 'Buildings')}</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#222', marginLeft: 4 }}>
              {loading ? <i className="fas fa-spinner fa-spin" style={{ fontSize: 16, color: '#aaa' }} /> : (buildingCount != null ? buildingCount.toLocaleString() : '-')}
            </span>
            <span style={{ fontSize: 14, color: '#888' }}>{l('อาคาร', 'buildings')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-calendar-alt" style={{ color: '#888', fontSize: 14 }} />
            <span style={{ fontSize: 13, color: '#888' }}>
              {l('ข้อมูล ณ วันที่', 'As of')} <strong style={{ color: '#555' }}>{dateStr}</strong>
            </span>
          </div>
        </div>

        {/* Stat rows */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
            <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#bbb' }} />
          </div>
        ) : (
          <div>
            {/* Always-visible rows: 0–3 */}
            {statRows.slice(0, 4).map((row, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #f5f5f5',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: `${row.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <i className={row.icon} style={{ color: row.color, fontSize: 14 }} />
                  </span>
                  <span style={{ fontSize: 14, color: '#444' }}>{row.label}</span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{row.value}</span>
                  <span style={{ fontSize: 12, color: '#888', marginLeft: 5 }}>{row.unit}</span>
                </div>
              </div>
            ))}

            {/* Collapsible rows: 4–7 */}
            <div style={{
              overflow: 'hidden',
              maxHeight: filterExpanded ? 400 : 0,
              opacity: filterExpanded ? 1 : 0,
              transition: 'max-height 0.3s ease, opacity 0.2s ease',
            }}>
              {statRows.slice(4).map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: idx < statRows.slice(4).length - 1 ? '1px solid #f5f5f5' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: `${row.color}18`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <i className={row.icon} style={{ color: row.color, fontSize: 14 }} />
                    </span>
                    <span style={{ fontSize: 14, color: '#444' }}>{row.label}</span>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{row.value}</span>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 5 }}>{row.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Toggle button */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: 10,
                cursor: 'pointer',
                color: '#888',
                userSelect: 'none',
                gap: 4,
                fontSize: 13,
              }}
              onClick={() => this.setState({ filterExpanded: !filterExpanded })}
            >
              <i className={`fas fa-chevron-${filterExpanded ? 'up' : 'down'}`} style={{ fontSize: 11 }} />
              <span>{filterExpanded ? l('ย่อ', 'Less') : l('ดูเพิ่มเติม', 'More')}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderFilterPanel() {
    const lang = this.props.i18n.language.split('-')[0];
    const l = (th, en) => (lang === 'th' ? th : en);
    const buildingTypeLabel = bt => bt[`name_${lang}`] || bt.name || bt.label || '';
    const purposeLabel = ep => ep[`name_${lang}`] || ep.name || ep.label || '';
    const { filterExpanded } = this.state;

    const filterHeader = (
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => this.setState({ filterExpanded: !filterExpanded })}
      >
        <span>{l('ตัวกรอง (Filter)', 'Filter')}</span>
        <i className={`fas fa-chevron-${filterExpanded ? 'up' : 'down'}`} style={{ color: '#888' }} />
      </div>
    );

    return (
      <Card header={filterHeader} padding={true}>
        <div ref={el => { this.filterRef = el; }}>
          <Form>
            {/* Always visible: building type, eval status, eval purpose */}
            <FormGroup>
              <ControlLabel>{l('ประเภทอาคาร', 'Building Type')}</ControlLabel>
              {this.renderMultiSelectDropdown({
                dropdownKey: 'buildingType',
                options: this.props.buildingTypes || [],
                selectedIds: this.state.selectedBuildingTypeIds,
                onToggle: ids => this.setState({ selectedBuildingTypeIds: ids }),
                getLabel: buildingTypeLabel,
              })}
            </FormGroup>
            <FormGroup>
              <ControlLabel>{l('สถานะการประเมิน', 'Evaluation Status')}</ControlLabel>
              {this.renderMultiSelectDropdown({
                dropdownKey: 'evalStatus',
                options: EVALUATION_STATUS_OPTIONS,
                selectedIds: this.state.selectedEvaluationStatuses,
                onToggle: ids => this.setState({ selectedEvaluationStatuses: ids }),
                getLabel: opt => l(opt.labelTh, opt.labelEn),
              })}
            </FormGroup>
            <FormGroup>
              <ControlLabel>{l('เหตุผลของการประเมิน', 'Evaluation Purpose')}</ControlLabel>
              {this.renderMultiSelectDropdown({
                dropdownKey: 'evalPurpose',
                options: this.props.evaluationPurposes || [],
                selectedIds: this.state.selectedEvaluationPurposeIds,
                onToggle: ids => this.setState({ selectedEvaluationPurposeIds: ids }),
                getLabel: purposeLabel,
              })}
            </FormGroup>

            {/* Collapsible extra filters: province, area, date */}
            <div style={{
              overflow: 'hidden',
              maxHeight: filterExpanded ? 400 : 0,
              opacity: filterExpanded ? 1 : 0,
              transition: 'max-height 0.3s ease, opacity 0.2s ease',
            }}>
              <FormGroup>
                <ControlLabel>{l('จังหวัด', 'Province')}</ControlLabel>
                {this.renderMultiSelectDropdown({
                  dropdownKey: 'province',
                  options: this.props.provinces || [],
                  selectedIds: this.state.selectedProvinceIds,
                  onToggle: ids => this.setState({ selectedProvinceIds: ids }),
                  getLabel: pv => pv.name || '',
                })}
              </FormGroup>
              <FormGroup>
                <ControlLabel>{l('พื้นที่อาคาร (ตร.ม.)', 'Building Area (m²)')}</ControlLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FormControl
                    type="number"
                    placeholder={l('ต่ำสุด', 'Min')}
                    value={this.state.areaMin}
                    onChange={e => this.setState({ areaMin: e.target.value })}
                    min={0}
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: '#666', flexShrink: 0 }}>–</span>
                  <FormControl
                    type="number"
                    placeholder={l('สูงสุด', 'Max')}
                    value={this.state.areaMax}
                    onChange={e => this.setState({ areaMax: e.target.value })}
                    min={0}
                    style={{ flex: 1 }}
                  />
                </div>
              </FormGroup>
              <FormGroup>
                <ControlLabel>{l('ช่วงวันที่', 'Date Range')}</ControlLabel>
                <FormControl
                  type="date"
                  value={this.state.dateFrom}
                  onChange={e => this.setState({ dateFrom: e.target.value })}
                  style={{ marginBottom: 6 }}
                />
                <ControlLabel style={{ fontWeight: 'normal', fontSize: 12, color: '#666' }}>
                  {l('ถึง', 'To')}
                </ControlLabel>
                <FormControl
                  type="date"
                  value={this.state.dateTo}
                  onChange={e => this.setState({ dateTo: e.target.value })}
                />
              </FormGroup>
            </div>

            {/* Always visible: action buttons */}
            <FormGroup style={{ marginBottom: 0, display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
              <Button bsStyle="primary" style={{ flex: 1 }} onClick={this.handleSearch}>
                {l('ค้นหา', 'Search')}
              </Button>
              <Button style={{ flex: 1 }} onClick={this.handleClearFilters}>
                {l('ล้างค่า', 'Clear')}
              </Button>
            </FormGroup>
          </Form>
        </div>
      </Card>
    );
  }

  renderBarChart(config) {
    const lang = this.props.i18n.language.split('-')[0];
    const l = (th, en) => (lang === 'th' ? th : en);
    const { savingSummary, loading } = this.state;

    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
          <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#888' }} />
        </div>
      );
    }

    const rawData = savingSummary ? savingSummary[config.key] : null;
    const labels = [];
    const values = [];

    if (rawData) {
      Object.entries(rawData).forEach(([k, v]) => {
        if (k !== 'all') {
          labels.push(k);
          values.push(config.convert(v));
        }
      });
    }

    const backgroundColors = labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);

    const chartData = {
      labels,
      datasets: [
        {
          label: l(config.titleTh, config.titleEn),
          data: values,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors,
          borderWidth: 1,
        },
      ],
    };

    const unit = l(config.unitTh, config.unitEn);
    const options = {
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        xAxes: [{
          scaleLabel: { display: true, labelString: l('ประเภทอาคาร', 'Building Type') },
          ticks: { autoSkip: false, maxRotation: 30, minRotation: 0 },
        }],
        yAxes: [{
          scaleLabel: { display: true, labelString: unit },
          ticks: {
            callback: val => val.toLocaleString(undefined, { maximumFractionDigits: 2 }),
          },
        }],
      },
      tooltips: {
        callbacks: {
          label: item => `${Number(item.yLabel).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`,
        },
      },
    };

    return (
      <Card padding={true}>
        <h4 style={{ marginTop: 0, fontWeight: 600, color: '#333' }}>
          {l(config.titleTh, config.titleEn)}
          <small style={{ marginLeft: 8, color: '#888', fontWeight: 400 }}>({unit})</small>
        </h4>
        {labels.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260, color: '#aaa' }}>
            {l('ไม่มีข้อมูล', 'No data')}
          </div>
        ) : (
          <div style={{ height: 280, position: 'relative' }}>
            <Bar data={chartData} options={options} height={280} />
          </div>
        )}
      </Card>
    );
  }

  render() {
    const lang = this.props.i18n.language.split('-')[0];
    const l = (th, en) => (lang === 'th' ? th : en);

    return (
      <Layout
        history={this.props.history}
        breadcrumbItems={[{ name: l('Dashboard ภาพรวม', 'Overview Dashboard'), path: '/executive_dashboard' }]}
        renderActionMenu={() => []}
      >
        <Grid>
          {/* Top section: summary cards (left) + filter (right) */}
          <Row className="show-grid" style={{ marginBottom: 16 }}>
            <Col xs={12} md={7}>
              <Card header={l('กราฟสรุปรวม', 'Summary')} padding={true}>
                {this.renderSummaryCards()}
              </Card>
            </Col>

            <Col xs={12} md={5}>
              {this.renderFilterPanel()}
            </Col>
          </Row>

          {/* Charts 2x2 */}
          <Row className="show-grid">
            <Col xs={12} md={6} style={{ marginBottom: 16 }}>
              {this.renderBarChart(BAR_CHART_CONFIGS[0])}
            </Col>
            <Col xs={12} md={6} style={{ marginBottom: 16 }}>
              {this.renderBarChart(BAR_CHART_CONFIGS[1])}
            </Col>
          </Row>
          <Row className="show-grid">
            <Col xs={12} md={6} style={{ marginBottom: 16 }}>
              {this.renderBarChart(BAR_CHART_CONFIGS[2])}
            </Col>
            <Col xs={12} md={6} style={{ marginBottom: 16 }}>
              {this.renderBarChart(BAR_CHART_CONFIGS[3])}
            </Col>
          </Row>
        </Grid>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  buildingTypes: state.buildingType.orderedIds.map(id => state.buildingType.buildingTypes[id]),
  provinces: state.province.orderedIds.map(id => state.province.provinces[id]),
  evaluationPurposes: state.reference.evaluationPurpose.evaluationPurposes,
});

const mapDispatchToProps = dispatch => ({
  listBuildingTypes: () => defaultRejectHandler(dispatch(listBuildingType())),
  listProvinces: () => defaultRejectHandler(dispatch(listProvinces())),
  listEvaluationPurpose: () => defaultRejectHandler(dispatch(listEvaluationPurpose())),
});

export default translate('translations')(
  connect(mapStateToProps, mapDispatchToProps)(ExecutiveDashboardPage),
);



// WEBPACK FOOTER //
// ./src/pages/executive_dashboard/ExecutiveDashboardPage.js